// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductTile from "@monsma/App/Components/ProductTile/ProductTile";
import Notify from "@monsma/XDL/Components/Notify";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
    // XNG-Changes
    showOverlayImageBadge?: boolean;
    showOverlayTextBadge?: boolean;
    maximumNumberOfOverlayBadges?: number;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 10,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        erpNumberLabelText: { variant: "legend" },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        customerNameLabelText: { variant: "legend" },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        manufacturerItemLabelText: { variant: "legend" },
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: { width: 12 },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },
    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    editingSortOrder,
    // XNG-Changes
    showOverlayImageBadge,
    showOverlayTextBadge,
    maximumNumberOfOverlayBadges,
}) => {
    const product = mapWishListLineToProduct(wishListLine);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    // xng-change: restructuring by using productTile
    const notifyMessage = () => (
        <>
            {isWishListLineRestricted(wishListLine, productInfo) && !isWishListLineDeactivated(wishListLine) && (
                <Notify
                    messageType="danger"
                    hideCloseButton={true}
                    body={siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}
                />
            )}
            {isWishListLineDeactivated(wishListLine) && (
                <Notify
                    messageType="danger"
                    hideCloseButton={true}
                    body={siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}
                />
            )}
        </>
    );

    const otherActions = () => (
        <>
            {canEditWishList && (
                <Link
                    className="wishlist-restricted-line-remove-link"
                    {...styles.deleteLink}
                    onClick={() => onDeleteClick(wishListLine)}
                    data-test-selector="removeRestricted"
                >
                    {translate("Remove")}
                </Link>
            )}
        </>
    );

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDeactivated(wishListLine)) {
        return (
            <>
                <ProductContext.Provider
                    value={productContext}
                    {...{ className: "list-view" }}
                    data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
                >
                    <StyledWrapper {...(styles as any).initialspaceWrapper}></StyledWrapper>
                    <ProductTile
                        showImage={true}
                        showBrand={false}
                        showTitle={true}
                        showPartNumbers={true}
                        showPrice={false}
                        showAddToCart={false}
                        showAddToList={false}
                        showAvailability={false}
                        showCompare={false}
                        showAttributes={false}
                        otherMiddleActions={notifyMessage}
                        otherActions={otherActions}
                        view="List"
                        extendedStyles={(styles as any).extendedProductTileStyles}
                        // XNG-Changes
                        showImageBadges={showImageBadges}
                        showOverlayImageBadge={showOverlayImageBadge}
                        showOverlayTextBadge={showOverlayTextBadge}
                        maximumNumberOfOverlayBadges={maximumNumberOfOverlayBadges}
                    />
                </ProductContext.Provider>
            </>
        );
    }

    const otherMiddleActions = () => (
        <StyledWrapper {...(styles as any).actionsAndNotesWrapper}>
            <StyledWrapper {...(styles as any).otherMiddleActionsWrapper}>
                {canEditWishList && (
                    <>
                        <Link
                            className="wishlist-line-edit-notes-link"
                            {...styles.editNotesLink}
                            onClick={() => onEditNotesClick(wishListLine)}
                            data-test-selector="myListDetails_addOrEditNote"
                        >
                            {translate(`${wishListLine.notes ? "Edit" : "Add"} Line Notes`)}
                        </Link>
                        <Link
                            {...styles.deleteLink}
                            className="wishlist-line-delete-link"
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="deleteListItem"
                        >
                            {translate("Remove")}
                        </Link>
                    </>
                )}
            </StyledWrapper>

            {wishListLine.notes && (
                <StyledWrapper {...(styles as any).notesWrapper}>
                    <SmallHeadingAndText
                        heading={translate("Notes:")}
                        text={wishListLine.notes}
                        extendedStyles={styles.notesStyles}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );

    const updateQtyActions = () => (
        <>
            {canEditWishList && (
                <StyledWrapper {...styles.updateQuantityWrapper}>
                    {productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                        <Link {...styles.updateQuantityLink} onClick={() => onUpdateQuantityClick()}>
                            {translate("Update QTY")}
                        </Link>
                    )}
                    {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                        <>
                            <Icon {...styles.quantityUpdatedIcon} />
                            <Typography {...styles.quantityUpdatedText}>{translate("QTY updated")}</Typography>
                        </>
                    )}
                </StyledWrapper>
            )}
        </>
    );
    return (
        <ProductContext.Provider
            value={productContext}
            {...{ className: "list-view" }}
            data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
        >
            <StyledWrapper {...(styles as any).selectedCheckboxWrapper}>
                <Checkbox
                    {...styles.selectedCheckbox}
                    checked={isSelected}
                    onChange={selectChangeHandler}
                    hiddenIconText={translate("Select")}
                    data-test-selector="myListDetails_selectRowCheckbox"
                >
                    {!editingSortOrder && <>{translate("Select")}</>}
                </Checkbox>
            </StyledWrapper>
            <ProductTile
                showImage={true}
                showBrand={true}
                showTitle={true}
                showPartNumbers={true}
                showPrice={true}
                showAddToCart={true}
                showAddToList={false}
                showAvailability={true}
                showCompare={false}
                showAttributes={false}
                otherMiddleActions={otherMiddleActions}
                updateQtyActions={updateQtyActions}
                view="List"
                priceShowSavings={true}
                priceShowSavingsAmount={productSettings.showSavingsAmount}
                priceShowSavingsPercent={productSettings.showSavingsPercent}
                extendedStyles={(styles as any).extendedProductTileStyles}
                productUnitOfMeasureDisabling={!canEditWishList}
                productNotes={wishListLine.notes}
                productQuantityDisabling={!canEditWishList}
                showTextBadges={showTextBadges}
                showImageBadges={showImageBadges}
                // XNG-Changes
                showOverlayImageBadge={showOverlayImageBadge}
                showOverlayTextBadge={showOverlayTextBadge}
                maximumNumberOfOverlayBadges={maximumNumberOfOverlayBadges}
            />
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine?.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
